import React from 'react'

import type { F0TimelineRowAction } from '@factorialco/f0-react'
import { Check, Cross, MessageFilled } from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  PurchaseRequestDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import RejectionReasonModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RejectionReasonModal'
import RequestChangesModal from 'modules/procurement/components/purchaseRequests/RequestChangesModal'

interface TimelineApprovalActionsRenderProps {
  primaryAction: F0TimelineRowAction
  secondaryActions: F0TimelineRowAction[]
}

interface TimelineApprovalActionsProps {
  reviewRequestIds: string[]
  children: (props: TimelineApprovalActionsRenderProps) => React.ReactNode
}

const TimelineApprovalActions = ({ reviewRequestIds, children }: TimelineApprovalActionsProps) => {
  const { company, employee } = useCurrentSession()
  const [requestChangesModalOpen, setRequestChangesModalOpen] = React.useState(false)
  const [rejectionModalOpen, setRejectionModalOpen] = React.useState(false)
  const [isUpdated, setIsUpdated] = React.useState(false)

  const [bulkApproveReviewRequest, { loading: approving }] = useMutation(
    BulkApproveApprovalsv2ReviewRequestDocument,
    {
      extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const handleApprove = async () => {
    const result = await bulkApproveReviewRequest({
      variables: { reviewRequestIds, companyId: company.id, reviewerId: employee.id },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.approve.error'),
      })
    })

    if (handled.success) {
      setIsUpdated(true)
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.detail.responses.approve.success_approved'),
      })
    }
  }

  const primaryAction: F0TimelineRowAction = {
    label: i18n.t('procurement.purchase_requests.detail.actions.approve'),
    icon: Check,
    onClick: handleApprove,
    disabled: approving || isUpdated,
  }

  const secondaryActions: F0TimelineRowAction[] = [
    {
      label: i18n.t('procurement.purchase_requests.detail.actions.changes_requested'),
      icon: MessageFilled,
      onClick: () => setRequestChangesModalOpen(true),
      disabled: approving || isUpdated,
    },
    {
      label: i18n.t('procurement.purchase_requests.detail.actions.reject'),
      icon: Cross,
      onClick: () => setRejectionModalOpen(true),
      disabled: approving || isUpdated,
    },
  ]

  return (
    <>
      {children({ primaryAction, secondaryActions })}

      <RequestChangesModal
        isOpen={requestChangesModalOpen}
        onClose={() => setRequestChangesModalOpen(false)}
        reviewRequestIds={reviewRequestIds}
        onSuccess={() => {
          setIsUpdated(true)
        }}
      />

      {rejectionModalOpen && (
        <RejectionReasonModal
          isOpen={rejectionModalOpen}
          onClose={() => setRejectionModalOpen(false)}
          reviewRequestIds={reviewRequestIds}
          onSuccess={() => {
            setIsUpdated(true)
            app.setFlash({
              type: 'correct',
              message: i18n.t('procurement.purchase_requests.detail.responses.reject.success'),
            })
          }}
        />
      )}
    </>
  )
}

export default TimelineApprovalActions
